import { Injectable } from '@angular/core';
import { Observable, of, map, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import {
  FirstLoginRequest,
  LoginRequest,
  ResetPasswordRequest,
  Verify2FARequest,
} from '../../models/auth/auth.model';
import { ApiService } from '../api/api.service';
import { StorageService } from '../api/storage.service';
import { CurrentUserService } from './current-user.service';
import { StorageKeys } from '../../../../enums/storage-keys.enum';
import { PermissionService } from '../user/permission.service';
import { HeaderService } from '../api/header.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private logoutSubject = new Subject<void>();
  public onLogout$ = this.logoutSubject.asObservable();

  private apiURL = `${environment.apiLocations.identity}/Auth`;

  private loginURL = `${this.apiURL}/login`;
  private forgotPasswordURL = `${this.apiURL}/forgotPassword`;
  private resetPasswordURL = `${this.apiURL}/resetPassword`;
  private firstLoginURL = `${this.apiURL}/firstLogin`;

  // Email Auth URLs
  private sendEmailAuthForEnableURL = `${this.apiURL}/sendEmailAuthenticatorForEnable`;
  private sendEmailAuthForDisableURL = `${this.apiURL}/sendEmailAuthenticatorForDisable`;
  private enableEmailAuthURL = `${this.apiURL}/enableEmailAuthenticator`;
  private disableEmailAuthURL = `${this.apiURL}/disableEmailAuthenticator`;
  private loginWithEmailAuthURL = `${this.apiURL}/loginWithEmailAuthenticator`;

  // Google Auth URLs
  private getGoogleAuthenticatorURL = `${this.apiURL}/getGoogleAuthenticator`;
  private enableGoogleAuthenticatorURL = `${this.apiURL}/enableGoogleAuthenticator`;
  private disableGoogleAuthenticatorURL = `${this.apiURL}/disableGoogleAuthenticator`;
  private loginWithGoogleAuthURL = `${this.apiURL}/loginWithGoogleAuthenticator`;

  private logoutURL = `${this.apiURL}/logout`;

  constructor(
    private readonly apiService: ApiService,
    private readonly storageService: StorageService,
    private readonly currentUserService: CurrentUserService,
    private readonly permissionService: PermissionService,
    private readonly headerService: HeaderService,
    private router: Router
  ) {}

  login(request: LoginRequest): Observable<any> {
    return new Observable((observer) => {
      this.apiService
        .post(
          this.loginURL,
          request,
          this.headerService.getHeadersWithoutAuth()
        )
        .subscribe({
          next: (res) => {
            if (!res.fail && res.data) {
              this.storageService.set(StorageKeys.USER, res.data);
              this.currentUserService.SetSession(res.data);
              this.permissionService.reload();
              observer.next(res.data);
            } else {
              observer.error(res);
            }
            observer.complete();
          },
          error: (err) => {
            observer.error(err);
          },
        });
    });
  }

  logout(): Observable<any> {
    return new Observable((observer) => {
      this.apiService.post(this.logoutURL, {}).subscribe({
        next: (res) => {
          this.logoutAndClear();
          observer.next(res);
          observer.complete();
        },
        error: (err) => {
          this.logoutAndClear();
          observer.error(err);
        },
      });
    });
  }

  forgotPassword(request: any): Observable<any> {
    return new Observable((observer) => {
      this.apiService
        .post(
          this.forgotPasswordURL,
          request,
          this.headerService.getHeadersWithoutAuth()
        )
        .subscribe({
          next: (res) => observer.next(res),
          error: (err) => observer.error(err),
        });
    });
  }

  resetPassword(request: ResetPasswordRequest): Observable<any> {
    return new Observable((observer) => {
      this.apiService
        .post(
          this.resetPasswordURL,
          request,
          this.headerService.getHeadersWithoutAuth()
        )
        .subscribe({
          next: (res) => observer.next(res),
          error: (err) => observer.error(err),
        });
    });
  }

  firstLogin(request: FirstLoginRequest): Observable<any> {
    return new Observable((observer) => {
      this.apiService
        .post(
          this.firstLoginURL,
          request,
          this.headerService.getHeadersWithoutAuth()
        )
        .subscribe({
          next: (res) => {
            if (!res.fail && res.data) {
              this.storageService.set(StorageKeys.USER, res.data);
              this.currentUserService.SetSession(res.data);
              this.permissionService.reload();
              observer.next(res.data);
            } else {
              observer.error(res);
            }
            observer.complete();
          },
          error: (err) => observer.error(err),
        });
    });
  }

  // ... Helper ...
  private logoutAndClear() {
    this.logoutSubject.next();
    this.storageService.clearAll();
    this.router.navigate(['/auth/login']);
  }

  // --- EMAIL AUTHENTICATOR ---
  sendEmailAuthForEnable(): Observable<any> {
    return new Observable((observer) => {
      this.apiService.post(this.sendEmailAuthForEnableURL, {}).subscribe({
        next: (res) => {
          if (!res.fail && res.data) {
            observer.next(res.data);
          } else {
            observer.error(res);
          }
          observer.complete();
        },
        error: (err) => observer.error(err),
      });
    });
  }

  sendEmailAuthForDisable(): Observable<any> {
    return new Observable((observer) => {
      this.apiService.post(this.sendEmailAuthForDisableURL, {}).subscribe({
        next: (res) => {
          !res.fail ? observer.next(res.data) : observer.error(res);
          observer.complete();
        },
        error: (err) => observer.error(err),
      });
    });
  }

  enableEmailAuth(request: { code: string }): Observable<any> {
    return new Observable((observer) => {
      this.apiService.post(this.enableEmailAuthURL, request).subscribe({
        next: (res) => {
          if (!res.fail) {
            observer.next(res.data);
          } else {
            observer.error(res);
          }
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

  disableEmailAuth(request: { code: string }): Observable<any> {
    return new Observable((observer) => {
      this.apiService.post(this.disableEmailAuthURL, request).subscribe({
        next: (res) => {
          if (!res.fail) {
            observer.next(res.data);
          } else {
            observer.error(res);
          }
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

  loginWithMailAuthenticator(request: Verify2FARequest): Observable<any> {
    return new Observable((observer) => {
      this.apiService
        .post(
          this.loginWithEmailAuthURL,
          request,
          this.headerService.getHeadersWithoutAuth()
        )
        .subscribe({
          next: (res) => {
            if (!res.fail && res.data) {
              this.storageService.set(StorageKeys.USER, res.data);
              this.currentUserService.SetSession(res.data);
              this.permissionService.reload();
              observer.next(res.data);
            } else {
              observer.error(res);
            }
            observer.complete();
          },
          error: (err) => observer.error(err),
        });
    });
  }

  // --- GOOGLE AUTHENTICATOR ---
  getGoogleAuth(): Observable<any> {
    return new Observable((observer) => {
      this.apiService.get(this.getGoogleAuthenticatorURL).subscribe({
        next: (res) => {
          if (!res.fail && res.data) {
            observer.next(res.data);
          } else {
            observer.error(res);
          }
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

  enableGoogleAuth(request: { code: string }): Observable<any> {
    return new Observable((observer) => {
      this.apiService
        .post(this.enableGoogleAuthenticatorURL, request)
        .subscribe({
          next: (res) => {
            if (!res.fail) {
              observer.next(res.data);
            } else {
              observer.error(res);
            }
            observer.complete();
          },
          error: (err) => {
            observer.error(err);
          },
        });
    });
  }

  disableGoogleAuth(request: { code: string }): Observable<any> {
    return new Observable((observer) => {
      this.apiService
        .post(this.disableGoogleAuthenticatorURL, request)
        .subscribe({
          next: (res) => {
            if (!res.fail) {
              observer.next(res.data);
            } else {
              observer.error(res);
            }
            observer.complete();
          },
          error: (err) => {
            observer.error(err);
          },
        });
    });
  }

  loginWithGoogleAuthenticator(request: Verify2FARequest): Observable<any> {
    return new Observable((observer) => {
      this.apiService
        .post(
          this.loginWithGoogleAuthURL,
          request,
          this.headerService.getHeadersWithoutAuth()
        )
        .subscribe({
          next: (res) => {
            if (!res.fail && res.data) {
              this.storageService.set(StorageKeys.USER, res.data);
              this.currentUserService.SetSession(res.data);
              this.permissionService.reload();
              observer.next(res.data);
            } else {
              observer.error(res);
            }
            observer.complete();
          },
          error: (err) => {
            observer.error(err);
          },
        });
    });
  }

  /** CHANGE PASSWORD */
  changePassword(request: {
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  }): Observable<any> {
    const changePasswordURL = `${this.apiURL}/changePassword`;
    return new Observable((observer) => {
      this.apiService.post(changePasswordURL, request).subscribe({
        next: (res) => {
          observer.next(res);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

  /**
   * Get valid access token for SignalR and other real-time connections
   * @returns The current user's access token
   */
  getValidAccessToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      const session = this.currentUserService.GetSession();
      if (session && session.token) {
        resolve(session.token);
      } else {
        reject('No valid token found');
      }
    });
  }
}
